import { forwardRef } from 'react';
import { Stack } from 'src/components/stack';
import { Tooltip } from 'src/components/tooltip';
import { labelsToOptions } from 'src/utils/labels-to-options';
import styled from 'styled-components';

export function assertDirection(value: string): asserts value is DirectionType {
  if (value === 'ENTRY' || value === 'EXIT' || value === 'BUNDLE') {
    type TypeCheck<T extends DirectionType = typeof value> = T;
  } else {
    throw new Error(`${value} is not of type "DirectionType".`);
  }
}

export function assertDirections(
  values: string[]
): asserts values is DirectionType[] {
  values.forEach(assertDirection);
}

export type FlowDirection = 'ENTRY' | 'EXIT';

export const flowDirectionLabels: Record<FlowDirection, string> = {
  ENTRY: 'Entry',
  EXIT: 'Exit',
};

export const flowDirectionOptions = labelsToOptions(flowDirectionLabels);

export type DirectionType = FlowDirection | 'BUNDLE';

export const directionLabels: Record<DirectionType, string> = {
  ...flowDirectionLabels,
  BUNDLE: 'Exit/Entry',
};

export const directionOptions = labelsToOptions(directionLabels);

export type DirectionProps =
  | {
      direction: FlowDirection;
      marketAreaName?: string;
    }
  | {
      direction: 'BUNDLE';
      marketAreaName?: [exit: string, entry: string];
    };

export const Direction = forwardRef<HTMLElement, DirectionProps>(
  ({ direction, marketAreaName }, ref) => {
    const tooltipContent = marketAreaName ? (
      <small>
        {Array.isArray(marketAreaName) ? (
          <Stack
            flow="column"
            templateColumns="1fr 1fr"
            gap={1}
            alignItems="start"
          >
            {marketAreaName[0] && (
              <span>
                <strong>{directionLabels['EXIT']}</strong>
                <br />
                {marketAreaName[0]}
              </span>
            )}
            {marketAreaName[1] && (
              <span>
                <strong>{directionLabels['ENTRY']}</strong>
                <br />
                {marketAreaName[1]}
              </span>
            )}
          </Stack>
        ) : (
          <Stack>
            <strong>{directionLabels[direction]}</strong>
            <>{marketAreaName}</>
          </Stack>
        )}
      </small>
    ) : null;

    if (tooltipContent) {
      return (
        <Tooltip content={tooltipContent}>
          {(targetProps) => (
            <Text
              ref={ref}
              data-testid={direction}
              direction={direction}
              {...targetProps}
            >
              {direction === 'BUNDLE' && 'Bundle'}
              {direction === 'ENTRY' && 'Entry'}
              {direction === 'EXIT' && 'Exit'}
            </Text>
          )}
        </Tooltip>
      );
    }

    return (
      <Text ref={ref} data-testid={direction} direction={direction}>
        {direction === 'BUNDLE' && 'Bundle'}
        {direction === 'ENTRY' && 'Entry'}
        {direction === 'EXIT' && 'Exit'}
      </Text>
    );
  }
);

const colors = {
  BUNDLE: '#7879B3',
  EXIT: '#CA85AC',
  ENTRY: '#56A7D8',
} as const;

const Text = styled.small<{ direction: DirectionType }>`
  font-weight: bold;
  color: ${({ direction }) => colors[direction]};
`;
